import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

interface PaymentStepLayoutProps {
  backLabel: string;
  onBack: () => void;
  title: string;
  /** Lets a step's own `role="radiogroup"` wrapper point `aria-labelledby` at this heading. */
  titleId?: string;
  subtitle: string;
  children: ReactNode;
  continueDisabled: boolean;
  onContinue: () => void;
  isLoading?: boolean;
  bottomBackLabel: string;
  noTopMargin?: boolean;
  continueVariant?: 'block' | 'pill';
}

export function PaymentStepLayout({
  backLabel,
  onBack,
  title,
  titleId,
  subtitle,
  children,
  continueDisabled,
  onContinue,
  isLoading = false,
  bottomBackLabel,
  noTopMargin = false,
  continueVariant = 'block'
}: Readonly<PaymentStepLayoutProps>) {
  const enabled = !continueDisabled && !isLoading;
  const isPill = continueVariant === 'pill';
  const shape = isPill
    ? 'rounded-full px-16 py-2'
    : 'w-full rounded-2xl px-2 py-4';

  return (
    <div className="w-full max-w-md px-6 sm:px-0">
      <div className={noTopMargin ? 'mt-0' : 'mt-8'}>
        <button
          type="button"
          onClick={onBack}
          className="-ml-1 mb-3 flex items-center gap-1 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>{backLabel}</span>
        </button>
        <h1 id={titleId} className="text-2xl font-bold text-gray-900">
          {title}
        </h1>
        <p className="mt-2 text-sm text-gray-500">{subtitle}</p>
      </div>

      <div className="mt-8 flex flex-col gap-3">{children}</div>

      <div className={isPill ? 'mt-6 flex justify-center' : 'mt-6'}>
        <button
          type="button"
          disabled={!enabled}
          onClick={onContinue}
          className={`${shape} font-semibold text-white transition-all duration-300 ease-out ${
            enabled
              ? 'bg-[#5CB891] hover:bg-[#4aa07c]'
              : 'cursor-not-allowed bg-gray-300'
          }`}
        >
          {isLoading ? 'Loading...' : 'Continue'}
        </button>
      </div>

      <div className="mt-4 flex justify-center">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-auto w-4" />
          <span>{bottomBackLabel}</span>
        </button>
      </div>
    </div>
  );
}
